// Where a slice's picture lands on the output for each lit surface: its quad there, the slice's input quad taken onto it.
import { quadToQuad } from '~/lib/homography'
import { anchorsOf } from '~/lib/outline/path'
import { isConvexQuad } from '~/lib/surfaceGeometry'
import { frameQuad, referenceFrame } from '~/lib/surfaceFrames'

/** A surface's `fit`. `window` shares the slice's spread (`slice.output`); `own` puts it on the surface alone: on `fit.quad` when placed by hand, else in perspective on four convex corners, else unwarped on its bounds. */
export const DEFAULT_FIT = { mode: 'window' }
export const OWN_FIT = { mode: 'own' }

/** Whether the surface's outline is a convex four-corner shape: the one an own picture warps onto. */
export const hasPerspective = path => isConvexQuad(anchorsOf(path))

const own = new WeakMap()

/** The quad an own picture lands on from the outline alone; null when it does not frame. */
const ownQuadOf = surface => {
	if (own.has(surface)) return own.get(surface)
	const anchors = anchorsOf(surface.path)
	const ref = { mode: isConvexQuad(anchors) ? 'perspective' : 'screen' }
	const frame = referenceFrame(ref, { d: surface.path, anchors }, null)
	const quad = frame && frameQuad(frame)
	own.set(surface, quad)
	return quad
}

/** The quad surface `s` shows its slice on, in output px; null when unlinked, cut or unframed. */
export const surfaceQuad = (data, s) => {
	const slice = s?.role === 'lit' && data.slices[s.slice]
	if (!slice) return null
	if (s.fit?.mode !== 'own') return slice.output
	return s.fit.quad ?? ownQuadOf(s)
}

/** Output px → frame fraction for surface `s`, or null. */
export const surfaceToFrame = (data, s) => {
	const q = surfaceQuad(data, s)
	return q && quadToQuad(q, data.slices[s.slice].input)
}

/** One draw per picture: `{ key, slice, quad, surfaces }`. A slice's windows share one; every other lit surface is its own. Hidden surfaces draw nothing. */
export const placements = data => {
	const out = new Map()
	for (const [id, s] of Object.entries(data.surfaces)) {
		if (s.hidden) continue
		const quad = surfaceQuad(data, s)
		if (!quad) continue
		const key = s.fit?.mode === 'own' ? id : s.slice
		if (!out.has(key)) out.set(key, { key, slice: s.slice, quad, surfaces: [] })
		out.get(key).surfaces.push(s)
	}
	return [...out.values()]
}
